import { useCallback, useState } from 'react';
import { Link, Text } from '@nimbus-ds/components';
import { AiFeature } from '@tiendanube/common/src/enums';
import { FEATURE_AI_TIER } from 'App/features';
import { AiFeedbackModal, Stack } from 'commons/components';
import { useDrawableCanvas } from 'commons/hooks';
import useAiFeedbackFlow from 'commons/hooks/useAiFeedbackFlow';
import { useAiUsage } from 'domains/Ai/hooks';
import useSmartErase from 'domains/Ai/hooks/useSmartErase';
import {
  trackingAiEditImageSmartEraseEraseClick,
  trackingAiEditImageSmartEraseUndoClick,
  trackingAiEditImageSmartEraseUseClick,
} from 'domains/Ai/tracking';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { ImageGalleryState } from '../../../../../ImageGallery';
import EditPhotoBaseModal from '../../../EditImageBaseModal';
import { EditedImage } from '../../../EditImageModal';

interface SmartEraseModalProps {
  currentImage: ImageGalleryState;
  onSaveImage: (editedImage: EditedImage) => void;
  isShow?: boolean;
  onClose?: () => void;
}

function SmartEraseModal({
  currentImage,
  onSaveImage,
  isShow = false,
  onClose,
}: Readonly<SmartEraseModalProps>) {
  const t = useTranslationCatalog(
    'products.modal.editPhoto.actions.smartErase',
  );

  const [canvasIsEmpty, setCanvasIsEmpty] = useState(true);

  const { canvasRef, getCanvasAsDataURL, clearCanvas, undoCanvas } =
    useDrawableCanvas();

  const {
    displayedImage,
    handleSmartErase,
    changingBackground,
    unSavedChanges,
    errorOnBackgroundChange,
    versions,
  } = useSmartErase(currentImage);

  const { refetchAiUses, requiredTier, aiUsesUpsellTitle } = useAiUsage();

  const eraseClick = async () => {
    const maskBase64 = getCanvasAsDataURL();
    clearCanvas();
    trackingAiEditImageSmartEraseEraseClick();
    await handleSmartErase(maskBase64);
    refetchAiUses();
  };

  const handleEraseClick = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_IMAGES_SMART_ERASE,
    callback: eraseClick,
    minValue: requiredTier,
    asAside: true,
  });

  const handleUndoClick = () => {
    undoCanvas();
    trackingAiEditImageSmartEraseUndoClick();
  };

  const handleCanvasDraw = useCallback(
    (isEmpty: boolean) => {
      setCanvasIsEmpty(isEmpty);
    },
    [setCanvasIsEmpty],
  );

  const handleOnSubmit = () => {
    if (unSavedChanges && displayedImage.base64) {
      onSaveImage({ base64: displayedImage.base64 });
      trackingAiEditImageSmartEraseUseClick({ versions });
    }
    if (onClose) {
      onClose();
    }
  };

  const [
    showAiGenerationFeedbackModal,
    openAiGenerationFeedbackModal,
    closeAiGenerationFeedbackModal,
  ] = useAiFeedbackFlow(AiFeature.AI_IMAGES_SMART_ERASE, onClose);

  const handleOnCloseWithFeedback = () => {
    if (unSavedChanges) {
      openAiGenerationFeedbackModal();
    } else {
      if (onClose) {
        onClose();
      }
    }
  };

  return (
    <>
      <EditPhotoBaseModal
        aiFeatureTrackingSource={AiFeature.AI_IMAGES_SMART_ERASE}
        isShow={isShow}
        canvasRef={canvasRef}
        onCanvasDraw={handleCanvasDraw}
        loadingImage={changingBackground}
        displayImageSource={displayedImage}
        onClose={handleOnCloseWithFeedback}
        translationPrefix="products.modal.editPhoto.actions.smartErase.modal"
        afterImageChildren={
          <Stack column align="stretch" spacing="tight">
            <Text color="neutral-textHigh">{t('modal.subtitle')}</Text>
            <Stack justify="flex-start" spacing="tight">
              <Link
                as="button"
                appearance="primary"
                onClick={handleEraseClick}
                disabled={changingBackground || canvasIsEmpty}
              >
                {t('modal.eraseCTA')}
              </Link>
              <Link
                as="button"
                appearance="neutral"
                onClick={handleUndoClick}
                disabled={changingBackground || canvasIsEmpty}
              >
                {t('modal.undoCTA')}
              </Link>
            </Stack>
          </Stack>
        }
        handleOnSave={handleOnSubmit}
        showAiUsage
        enableSaveButton={
          unSavedChanges && !changingBackground && !errorOnBackgroundChange
        }
      />
      <AiFeedbackModal
        asAside
        show={showAiGenerationFeedbackModal}
        title={t('modal.feedback.title')}
        text={t('modal.feedback.text')}
        feature={AiFeature.AI_IMAGES_SMART_ERASE}
        feedbackParams={{
          imageEditing: {
            oldImageUrl: currentImage.publicUrl!,
            newImageBase64: displayedImage.base64!,
          },
        }}
        onDismiss={() => {
          closeAiGenerationFeedbackModal();
          if (onClose) {
            onClose();
          }
        }}
      />
    </>
  );
}

export default SmartEraseModal;
